import moment from 'moment'
import PropTypes from 'prop-types'
import { Component } from 'react'
import { FormattedMessage } from 'react-intl'

import ConfirmationCard from '@/components/ConfirmationCard'
import { Clock } from '@/components/Icons'

import messages from './messages'

import './index.scss'

export const isExpired = (expirationMoment: any) => {
  return moment().isSameOrAfter(expirationMoment)
}

export const isExpiringSoon = (expirationMoment: any) => {
  return moment().isSameOrAfter(expirationMoment.clone().subtract(2, 'minutes'))
}

export default class CaptureSessionTimeout extends Component<any, any> {
  static displayName = 'CaptureSessionTimeout'

  static propTypes = {
    expirationMoment: PropTypes.object,
    isAuthenticated: PropTypes.bool,
    refreshSession: PropTypes.func.isRequired
  }

  static defaultProps = {
    expirationMoment: null,
    isAuthenticated: false
  }

  expiredTimer: any
  warningTimer: any

  state = {
    ignoreWarning: false,
    refreshingSession: false
  }

  componentDidMount() {
    this.setWarningTimer()
    this.setExpiredTimer()
  }

  componentDidUpdate(prevProps: any) {
    if (prevProps.expirationMoment !== this.props.expirationMoment) {
      this.setState({ ignoreWarning: false, refreshingSession: false })
      this.setWarningTimer()
      this.setExpiredTimer()
    }
  }

  componentWillUnmount() {
    if (this.warningTimer) clearTimeout(this.warningTimer)
    if (this.expiredTimer) clearTimeout(this.expiredTimer)
  }

  onWarningCancel = () => this.setState({ ignoreWarning: true })

  /**
   * There can be a slight delay after the user requests a new session that gives the appearance the UI is unresponsive.
   * The delay likely stems from persisting the entire Redux state tree to client storage. This mitigates the delay by
   * hiding the expired session modal.
   */
  refreshSession = () => {
    this.setState({ refreshingSession: true })
    this.props.refreshSession()
  }

  setExpiredTimer = () => {
    const { expirationMoment } = this.props

    if (this.expiredTimer) clearTimeout(this.expiredTimer)
    if (!this.props.isAuthenticated || !expirationMoment) return

    this.expiredTimer = setTimeout(
      this.validateSession,
      // ensure a minimum delay of at least 0 milliseconds
      Math.max(0, expirationMoment.clone().subtract(2, 'minutes').valueOf() - moment().valueOf())
    )
  }

  setWarningTimer = () => {
    const { expirationMoment } = this.props

    if (this.warningTimer) clearTimeout(this.warningTimer)
    if (!this.props.isAuthenticated || !expirationMoment) return

    this.warningTimer = setTimeout(
      this.validateSession,
      // ensure a minimum delay of at least 0 milliseconds
      Math.max(0, expirationMoment.valueOf() - moment().valueOf())
    )
  }

  validateSession = () => {
    if (this.props.isAuthenticated && isExpiringSoon(this.props.expirationMoment)) {
      // force React to rerender this component in order to display the appropriate modal
      this.forceUpdate()
    }
  }

  render() {
    const { expirationMoment } = this.props

    if (!this.state.refreshingSession && this.props.isAuthenticated && expirationMoment) {
      // MUST check for outright expiration first otherwise an expired session will always present as expiring "soon"
      if (isExpired(expirationMoment)) {
        return (
          <div className="confirmation__overlay add-space session-timeout">
            <div className="confirmation__box">
              <ConfirmationCard
                error
                // @ts-expect-error
                icon={<Clock />}
                message={<FormattedMessage {...messages.title} />}
                modal
                primaryClick={this.refreshSession}
                primaryText={<FormattedMessage {...messages.refresh} />}
                subMessage={
                  <span>
                    <FormattedMessage {...messages.part1} />{' '}
                    <strong>
                      <FormattedMessage {...messages.refreshLowerCase} />{' '}
                    </strong>
                    <FormattedMessage {...messages.part2} />
                  </span>
                }
              />
            </div>
          </div>
        )
      } else if (isExpiringSoon(expirationMoment) && !this.state.ignoreWarning) {
        return (
          <div className="confirmation__overlay add-space session-timeout">
            <div className="confirmation__box">
              <ConfirmationCard
                error
                // @ts-expect-error
                icon={<Clock />}
                message={<FormattedMessage {...messages.warning} />}
                modal
                primaryClick={this.refreshSession}
                primaryText={<FormattedMessage {...messages.refresh} />}
                secondaryClick={this.onWarningCancel}
                secondaryText={<FormattedMessage {...messages.cancel} />}
                subMessage={
                  <>
                    <span>
                      <FormattedMessage {...messages.twoMinutes} />
                    </span>
                    <br />
                    <br />
                    <FormattedMessage {...messages.click} />{' '}
                    <strong>
                      <FormattedMessage {...messages.refreshLowerCase} />
                    </strong>{' '}
                    <FormattedMessage {...messages.acquire} />
                  </>
                }
              />
            </div>
          </div>
        )
      }
    }

    return null
  }
}
